import React from 'react';
import { NOTE_CATEGORIES, TEACHING_METHODS, TEACHING_UNDERSTANDING } from '../../../../../dtos/patientmr/charting/general-note.dto';
import type { NoteCategory } from '../../../../../dtos/patientmr/charting';
import type { NoteFormData, TeachingFormData } from '../../types';
import { codeLabel, NOTE_CATEGORY_LABELS } from '../../model/entryMeta';
import { FieldsProps, InputField, SelectField, TextAreaField } from './fieldParts';
import { VisibilityToggle } from './SmallControls';

export const TEACHING_UNDERSTANDING_LABELS: Record<string, string> = {
  VERBALIZED: 'Verbalized understanding',
  RETURN_DEMONSTRATION: 'Return demonstration',
  NEEDS_REINFORCEMENT: 'Needs reinforcement',
};

/** Phase 18 M13 category, Phase 19 F22 teaching record, Phase 18 M6 visibility */
const NoteFields: React.FC<FieldsProps<NoteFormData> & { firstRef?: React.Ref<HTMLTextAreaElement> }> = ({
  value, onChange, errors, showRequired, isMobile, disabled, firstRef,
}) => {
  const common = { isMobile, disabled, showRequired, labelClassName: 'text-purple-700' };
  const setTeaching = (field: keyof TeachingFormData) => (v: string) => onChange({ ...value, teaching: { ...value.teaching, [field]: v } });
  const t = value.teaching;
  return (
    <div className="space-y-2">
      <SelectField {...common} id="note-category" label="Category" placeholder="General"
        options={NOTE_CATEGORIES.filter((c) => c !== 'GENERAL').map((c) => ({ value: c, label: NOTE_CATEGORY_LABELS[c] }))}
        value={value.category === 'GENERAL' ? '' : value.category}
        onChange={(v) => onChange({ ...value, category: (v || 'GENERAL') as NoteCategory })} />
      {value.category === 'TEACHING' && (
        <div className="grid grid-cols-1 gap-2 rounded border border-purple-200 bg-white/60 p-2 md:grid-cols-2" data-testid="teaching-fields">
          <InputField {...common} id="teaching-subject" label="Subject" required placeholder="e.g. Trach care, G-tube feeding"
            value={t.subject} onChange={setTeaching('subject')} error={errors['teaching.subject']} />
          <InputField {...common} id="teaching-learner" label="Learner" required placeholder="e.g. Patient, mother, caregiver"
            value={t.learner} onChange={setTeaching('learner')} error={errors['teaching.learner']} />
          <SelectField {...common} id="teaching-method" label="Method" required
            options={TEACHING_METHODS.map((m) => ({ value: m, label: codeLabel(m) }))}
            value={t.method} onChange={setTeaching('method')} error={errors['teaching.method']} />
          <InputField {...common} id="teaching-duration" label="Duration (minutes)" required type="number" placeholder="1–480"
            value={t.durationMinutes} onChange={setTeaching('durationMinutes')} error={errors['teaching.durationMinutes']} />
          <SelectField {...common} id="teaching-understanding" label="Understanding" required className="md:col-span-2"
            options={TEACHING_UNDERSTANDING.map((u) => ({ value: u, label: TEACHING_UNDERSTANDING_LABELS[u] }))}
            value={t.understanding} onChange={setTeaching('understanding')} error={errors['teaching.understanding']} />
        </div>
      )}
      <TextAreaField {...common} id="note-text" label="Note" required inputRef={firstRef} rows={isMobile ? 8 : 6}
        placeholder="Observations, assessment, plan..." value={value.note}
        onChange={(v) => onChange({ ...value, note: v })} error={errors.note} />
      <VisibilityToggle id="note-internal" disabled={disabled} internal={value.visibility === 'INTERNAL'}
        onChange={(internal) => onChange({ ...value, visibility: internal ? 'INTERNAL' : 'CHART' })} />
    </div>
  );
};

export default NoteFields;
